// prettier-ignore
import { Box, Card, CardContent, CardHeader, Avatar, List, ListItem, Paper, TextField, FormHelperText, Button, Stack, MenuItem, FormControl, InputLabel, InputAdornment, Select } from "@mui/material";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import NotificationsIcon from "@mui/icons-material/Notifications";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useHistory } from "react-router-dom";
import { SubmitButton } from "../../common/forms/submitButton";
import { useSelector } from "react-redux";
import { formatDateFromUTC } from "../../utils/FormatDateFromUTC";
import DateTimePicker from "../../common/forms/dateTimePicker";
import notificationsService from "../../../services/notifications.service";
import { useSnackbar } from "notistack";

export const NewNotificationForm = ({ distributors }) => {
  const { user } = useSelector((state) => state.auth);
  const { email, role } = user;

  const initialValues = {
    category: "",
    title: "",
    msg: "",
    target: "",
    start_date: "",
    end_date: "",
    create_user: email,
  };

  const schema = Yup.object().shape({
    category: Yup.string().required("Campo requerido"),
    title: Yup.string()
      .required("Campo requerido")
      .max(250, "El mensaje no puede superar los 250 caracteres"),
    msg: Yup.string()
      .required("Campo requerido")
      .max(1000, "El mensaje no puede superar los 1000 caracteres"),
    target: Yup.string().required("Campo requerido"),
    start_date: Yup.date()
      .typeError("Fecha inválida")
      .test(
        "start_date_not_past",
        "La fecha de inicio no puede ser anterior a la fecha y hora actual",
        function (value) {
          const start = value || new Date();
          return start >= new Date(Date.now() - 5 * 60 * 1000);
        }
      ),
    end_date: Yup.date()
      .required("Campo requerido")
      .typeError("Fecha inválida")
      .min(
        new Date(),
        "La fecha de fin debe ser posterior a la fecha y hora actual"
      )
      .test(
        "end_date_after_start_date",
        "La fecha de fin debe ser posterior a la de inicio",
        function (value) {
          const { start_date } = this.parent;
          if (!start_date) return true; // No validar si no hay start_date
          if (!value) return false;
          return value > new Date(start_date);
        }
      )
      .test(
        "min_3h_period",
        "La fecha de fin debe ser al menos 3 horas posterior a la de inicio",
        function (value) {
          let { start_date } = this.parent;
          if (!start_date) start_date = new Date();
          if (!value) return false;
          return (
            value.getTime() - new Date(start_date).getTime() >=
            3 * 60 * 60 * 1000
          );
        }
      ),
  });

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const handleSubmit = async (values, { setSubmitting }) => {
    console.log("values", values);

    try {
      if (!values.start_date) {
        values.start_date = Date.now();
      } else if (!(values.start_date instanceof Date)) {
        values.start_date = new Date(values.start_date).getTime();
      } else {
        values.start_date = values.start_date.getTime();
      }
      values.end_date = values.end_date.getTime();
      await notificationsService.insert(values, role);
      enqueueSnackbar("Notificación creada correctamente", {
        variant: "success",
      });
      history.push("/notifications");
    } catch (error) {
      console.error("Error al crear la notificación:", error);
      enqueueSnackbar("Error al crear la notificación", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={schema}
      validateOnChange={true}
      validateOnBlur={true}
    >
      {({
        handleChange,
        handleBlur,
        setFieldValue,
        validateForm,
        values,
        isSubmitting,
        isValid,
        touched,
        errors,
      }) => (
        <Form>
          {console.log("values", values)}
          <Box sx={{ maxWidth: 680 }}>
            <Card sx={{ mb: 2 }}>
              <Paper>
                <CardHeader
                  sx={{
                    pt: 3,
                    pb: 0,
                    "& .MuiCardHeader-action": { my: 0, mx: 2 },
                  }}
                  avatar={
                    <Avatar
                      aria-label="notification"
                      sx={{ width: 42, height: 42, ml: 2 }}
                    >
                      <NotificationsIcon />
                    </Avatar>
                  }
                  title="Notificación"
                  subheader={`${formatDateFromUTC(Date.now())} por ${email}`}
                ></CardHeader>
                <CardContent>
                  <List dense={true} sx={{ pb: 0 }}>
                    <ListItem sx={{ mb: 1 }}>
                      <FormControl sx={{ width: "100%" }}>
                        <InputLabel
                          id="category-label"
                          error={touched.category && Boolean(errors.category)}
                        >
                          Categoría
                        </InputLabel>
                        <Field
                          component={Select}
                          labelId="category-label"
                          id="category"
                          name="category"
                          label="Categoría"
                          value={values.category}
                          onChange={handleChange("category")}
                          error={touched.category && Boolean(errors.category)}
                          size="small"
                        >
                          {[
                            { text: "Informativa", val: "INFO" },
                            { text: "Alerta", val: "WARNING" },
                          ].map((cat) => (
                            <MenuItem key={cat.val} value={cat.val}>
                              {cat.text}
                            </MenuItem>
                          ))}
                        </Field>
                        {touched.category && (
                          <FormHelperText htmlFor="category" error>
                            {errors.category}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </ListItem>
                    <ListItem sx={{ mb: 1 }}>
                      <FormControl sx={{ width: "100%" }}>
                        <InputLabel id="target-label">Distribuidor</InputLabel>
                        <Field
                          component={Select}
                          labelId="target-label"
                          id="target"
                          name="target"
                          label="Distribuidora"
                          value={values.target}
                          fullWidth
                          onChange={handleChange("target")}
                          size="small"
                          error={touched.target && Boolean(errors.target)}
                          endAdornment={
                            values.target && (
                              <InputAdornment position="end">
                                <IconButton
                                  sx={{ padding: 0.5, mr: 2.3 }}
                                  onClick={() => {
                                    setFieldValue("target", "");
                                  }}
                                >
                                  <ClearIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </InputAdornment>
                            )
                          }
                        >
                          {distributors &&
                            distributors.length > 0 &&
                            distributors.map((dist) => {
                              return (
                                <MenuItem key={dist.id} value={dist.id}>
                                  {dist.name}
                                </MenuItem>
                              );
                            })}
                        </Field>
                        {touched.category && (
                          <FormHelperText htmlFor="distributor" error>
                            {errors.distributor}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </ListItem>
                    <ListItem sx={{ mb: 1 }}>
                      <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
                        <Field
                          component={DateTimePicker}
                          name="start_date"
                          label="Fecha inicio"
                        />
                        {
                          <Box sx={{ mx: 2, alignContent: "center" }}>
                            {" "}
                            {"hasta"}{" "}
                          </Box>
                        }
                        <Field
                          component={DateTimePicker}
                          name="end_date"
                          label="Fecha fin"
                        />
                      </Stack>
                    </ListItem>
                    <ListItem sx={{ mb: 1 }}>
                      <TextField
                        fullWidth
                        id="title"
                        name="title"
                        label="Título"
                        value={values.title}
                        onChange={handleChange("title")}
                        onBlur={handleBlur("title")}
                        size="small"
                        error={touched.title && Boolean(errors.title)}
                        helperText={
                          touched.title && errors.title ? errors.title : ""
                        }
                      ></TextField>
                    </ListItem>
                    <ListItem sx={{ mb: 1 }}>
                      <TextField
                        fullWidth
                        multiline
                        maxRows={2}
                        id="msg"
                        name="msg"
                        label="Mensaje"
                        value={values.text}
                        onChange={handleChange("msg")}
                        onBlur={handleBlur("msg")}
                        error={touched.text && Boolean(errors.text)}
                        helperText={touched.msg && errors.msg ? errors.msg : ""}
                      ></TextField>
                    </ListItem>
                  </List>
                </CardContent>
              </Paper>
            </Card>
            <Stack spacing={1} direction="row">
              <Button
                onClick={() => {
                  history.push("/notifications");
                }}
                startIcon={<ArrowBackIcon />}
              >
                Volver
              </Button>
              <SubmitButton
                text="Guardar"
                isValid={isValid}
                isSubmitting={isSubmitting}
              ></SubmitButton>
            </Stack>
          </Box>
        </Form>
      )}
    </Formik>
  );
};
